import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useHttp from '../../../hooks/use-http';
import i18n from '../../../i18n';
import FieldRenderer from '../../../components/Form/Fields/FieldRenderer';
import './ProductStoreFilters.scss';

const FILTER_API = '/rest/product/get_filter_store/';

/** These filter UIs always use `__in`; users must not switch to another lookup. */
const TEMPLATES_LOCK_TO_IN = new Set(['select_search', 'select', 'radio_cards', 'checkbox']);

const matchesLockToInTemplate = (...candidates) =>
    candidates.some((c) => c != null && c !== '' && TEMPLATES_LOCK_TO_IN.has(String(c)));

/**
 * Lock `__in` only when this field's own template is a multi-select UI.
 * Do not use lookup_types.in.template — NumberFilter (and others) advertise an
 * `in` option with select_search, which incorrectly forced max_price__in etc.
 */
const lockLookupToIn = (fieldConfig) => {
    const lookupTypes = fieldConfig?.lookup_types || {};
    if (!lookupTypes.in) return false;
    return matchesLockToInTemplate(fieldConfig.template, fieldConfig.type);
};

const normalizeToArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined || value === '') return [];
    return [value];
};

const toggleManyChoiceValue = (current, toggleVal) => {
    const arr = normalizeToArray(current);
    const s = String(toggleVal);
    const idx = arr.findIndex((x) => String(x) === s);
    if (idx >= 0) arr.splice(idx, 1);
    else arr.push(toggleVal);
    return arr;
};

const parseSearchParamsToFilterState = (search, { allowedFields = new Set(), knownFilters = {} } = {}) => {
    const values = {};
    const lookups = {};

    const raw = typeof search === 'string' ? search : '';
    const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);

    // Group query params by their raw key to preserve repeated params.
    const byKey = {};
    for (const [k, v] of params.entries()) {
        if (!byKey[k]) byKey[k] = [];
        byKey[k].push(v);
    }

    Object.entries(byKey).forEach(([rawKey, allVals]) => {
        if (!rawKey) return;

        let fieldName = rawKey;
        let lookup = '';

        const idx = rawKey.lastIndexOf('__');
        if (idx > 0) {
            fieldName = rawKey.slice(0, idx);
            lookup = rawKey.slice(idx + 2);
        }

        if (allowedFields.size > 0 && !allowedFields.has(fieldName)) return;

        let v = allVals.length > 1 ? allVals : allVals[0];
        const cfg = knownFilters[fieldName];
        // many / __in must stay arrays so choice selection (and toggles) stay in sync
        if (cfg?.many || lookup === 'in' || lockLookupToIn(cfg || {})) {
            v = normalizeToArray(v);
        }

        values[fieldName] = v;
        lookups[fieldName] = lookup;

        // Ignore invalid lookups if we know the field.
        if (cfg?.lookup_types && !Object.prototype.hasOwnProperty.call(cfg.lookup_types, lookup)) {
            lookups[fieldName] = '';
        }
    });

    return { values, lookups };
};

const buildSearchParamsFromFilterState = ({ filterValues = {}, resolvedLookups = {} } = {}) => {
    const params = new URLSearchParams();

    Object.entries(filterValues).forEach(([fieldName, value]) => {
        const lookup = resolvedLookups[fieldName] ?? '';
        const paramName = lookup ? `${fieldName}__${lookup}` : fieldName;

        if (value === null || value === undefined || value === '') return;

        // For __in lookups, allow arrays => repeated query params.
        if (lookup === 'in' && Array.isArray(value)) {
            value.forEach((v) => {
                if (v !== null && v !== undefined && v !== '') params.append(paramName, v);
            });
            return;
        }

        params.append(paramName, value);
    });

    const qs = params.toString();
    return qs ? `?${qs}` : '';
};

const getCurrentSearch = (fallback = '') => {
    if (typeof window === 'undefined') return fallback || '';
    return window.location.search || fallback || '';
};

const buildInitialFilterState = (filtersData = {}, search = '') => {
    if (!filtersData || Object.keys(filtersData).length === 0) {
        return { values: {}, lookups: {} };
    }

    const initialValues = {};
    const initialLookups = {};
    Object.keys(filtersData).forEach((key) => {
        if (filtersData[key] && filtersData[key].value !== undefined) {
            let v = filtersData[key].value;
            if (filtersData[key].many || lockLookupToIn(filtersData[key])) {
                v = normalizeToArray(v);
            }
            initialValues[key] = v;
        }
        initialLookups[key] = '';
    });

    const allowedFields = new Set(Object.keys(filtersData));
    const fromUrl = parseSearchParamsToFilterState(search, {
        allowedFields,
        knownFilters: filtersData,
    });

    return {
        values: { ...initialValues, ...fromUrl.values },
        lookups: { ...initialLookups, ...fromUrl.lookups },
    };
};

const normalizeSelectOptions = (options) => {
    if (!Array.isArray(options)) return [];
    return options.map((opt) => {
        if (opt && typeof opt === 'object') {
            return {
                value: opt.value ?? opt.id ?? '',
                label: opt.display ?? opt.label ?? opt.value ?? opt.id ?? '',
            };
        }
        return { value: opt, label: String(opt) };
    });
};

const choiceFilterGroupName = (fieldName) => `store-filter-${fieldName}`;

const optionLabelForValue = (selectOptions, value) => {
    const found = selectOptions.find((o) => String(o.value) === String(value));
    return found ? found.label : String(value);
};

const isMultiValueFilter = (fieldConfig) => {
    if (!fieldConfig) return false;
    const template = fieldConfig.template || fieldConfig.type || '';
    if (fieldConfig.many) return true;
    // radio_cards / checkbox store filters are multi-select in the shop UI
    if (template === 'radio_cards' || template === 'checkbox' || template === 'checkbox_cards') {
        return true;
    }
    return lockLookupToIn(fieldConfig);
};

const emptyValueForFilter = (fieldConfig) => (isMultiValueFilter(fieldConfig) ? [] : '');

const isEmptyFilterValue = (value) => {
    if (value === null || value === undefined || value === '') return true;
    if (Array.isArray(value)) return value.length === 0;
    return false;
};

const rangeCeiling = (fieldConfig) => {
    const n = Number(fieldConfig?.max);
    return Number.isFinite(n) ? n : null;
};

/** Unset max price sits at the catalog maximum and is not an active filter. */
const maxPriceDisplayValue = (fieldName, fieldConfig, stored) => {
    if (fieldName !== 'max_price' || !isEmptyFilterValue(stored)) return stored;
    const ceiling = rangeCeiling(fieldConfig);
    return ceiling == null ? stored : ceiling;
};

const isDefaultMaxPrice = (fieldName, fieldConfig, value) => {
    if (fieldName !== 'max_price') return false;
    const ceiling = rangeCeiling(fieldConfig);
    if (ceiling == null || isEmptyFilterValue(value)) return false;
    return Number(value) >= ceiling;
};

const getFilterFieldValue = (filterValues, fieldName, fieldConfig) => {
    // Prefer explicit state (including [] / '') so SSR fieldConfig.value cannot resurrect a cleared selection.
    if (Object.prototype.hasOwnProperty.call(filterValues, fieldName)) {
        const v = filterValues[fieldName];
        return isMultiValueFilter(fieldConfig) ? normalizeToArray(v) : v;
    }
    const fallback = fieldConfig?.value;
    if (fallback === undefined || fallback === null || fallback === '') {
        return emptyValueForFilter(fieldConfig);
    }
    return isMultiValueFilter(fieldConfig) ? normalizeToArray(fallback) : fallback;
};

/** Collapse choice options to the first flex row until expanded. */
const CollapsibleChoiceOptions = ({ children, measureKey, role, 'aria-label': ariaLabel }) => {
    const containerRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [canCollapse, setCanCollapse] = useState(false);
    const [rowMaxHeight, setRowMaxHeight] = useState(null);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el || typeof window === 'undefined') return undefined;

        const measure = () => {
            const prevMax = el.style.maxHeight;
            const prevOverflow = el.style.overflow;
            el.style.maxHeight = 'none';
            el.style.overflow = 'visible';

            const first = el.children[0];
            if (!first) {
                setCanCollapse(false);
                setRowMaxHeight(null);
                el.style.maxHeight = prevMax;
                el.style.overflow = prevOverflow;
                return;
            }

            const styles = window.getComputedStyle(el);
            const gap = parseFloat(styles.rowGap || styles.gap || '0') || 0;
            const rowH = first.getBoundingClientRect().height;
            const needsCollapse = el.scrollHeight > rowH + gap + 2;

            setRowMaxHeight(rowH);
            setCanCollapse(needsCollapse);

            el.style.maxHeight = prevMax;
            el.style.overflow = prevOverflow;
        };

        measure();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (ro) ro.observe(el);
        window.addEventListener('resize', measure);
        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [measureKey]);

    const collapsedStyle =
        canCollapse && !expanded && rowMaxHeight != null
            ? { maxHeight: rowMaxHeight, overflow: 'hidden' }
            : undefined;

    return (
        <div>
            <div
                ref={containerRef}
                className="d-flex flex-wrap gap-1"
                style={collapsedStyle}
                role={role}
                aria-label={ariaLabel}
            >
                {children}
            </div>
            {canCollapse ? (
                <button
                    type="button"
                    className="btn btn-link btn-sm px-0 mt-1"
                    onClick={() => setExpanded((v) => !v)}
                >
                    {expanded ? i18n.t('Show less') : i18n.t('Show more')}
                </button>
            ) : null}
        </div>
    );
};

export default function ProductStoreFilters({ initialData = null }) {
    const initialSearch = getCurrentSearch(initialData?.search || '');
    const initialFilters = initialData?.filters || {};
    const initialFilterState = buildInitialFilterState(initialFilters, initialSearch);

    const [filters, setFilters] = useState(() => initialFilters);
    const [filterValues, setFilterValues] = useState(() => initialFilterState.values);
    const [filterLookups, setFilterLookups] = useState(() => initialFilterState.lookups);
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);
    const [searchText, setSearchText] = useState(() => {
        const params = new URLSearchParams((initialSearch || '').startsWith('?') ? initialSearch.slice(1) : (initialSearch || ''));
        return params.get('search') || '';
    });

    const [urlSearch, setUrlSearch] = useState(() => initialSearch);
    const syncingFromUrlRef = useRef(false);
    const pushStateDebounceRef = useRef(null);

    const { isLoading: filtersLoading, error: filtersError, sendRequest: fetchFilters } = useHttp((data) => {
        setFilters(data || {});
        if (data) {
            const nextFilterState = buildInitialFilterState(data, getCurrentSearch());
            syncingFromUrlRef.current = true;
            setFilterValues(nextFilterState.values);
            setFilterLookups(nextFilterState.lookups);
            setUrlSearch(getCurrentSearch());
            setTimeout(() => {
                syncingFromUrlRef.current = false;
            }, 0);
        }
    });

    const resolvedLookups = useMemo(() => {
        const out = { ...filterLookups };
        Object.entries(filters).forEach(([name, cfg]) => {
            if (lockLookupToIn(cfg)) {
                out[name] = 'in';
            }
        });
        return out;
    }, [filters, filterLookups]);

    useEffect(() => {
        if (filters && Object.keys(filters).length > 0) return;
        fetchFilters({ url: FILTER_API, json: true });
    }, [filters, fetchFilters]);

    useEffect(() => {
        const onLocationChange = () => setUrlSearch(window.location.search || '');
        window.addEventListener('popstate', onLocationChange);
        window.addEventListener('locationchange', onLocationChange);
        return () => {
            window.removeEventListener('popstate', onLocationChange);
            window.removeEventListener('locationchange', onLocationChange);
        };
    }, []);

    // URL -> filter state (supports back/forward and initial load).
    useEffect(() => {
        if (!filters || Object.keys(filters).length === 0) return;

        const allowedFields = new Set(Object.keys(filters));
        const fromUrl = parseSearchParamsToFilterState(urlSearch || '', {
            allowedFields,
            knownFilters: filters,
        });

        const params = new URLSearchParams((urlSearch || '').startsWith('?') ? (urlSearch || '').slice(1) : (urlSearch || ''));
        const nextSearchText = params.get('search') || '';

        syncingFromUrlRef.current = true;
        setFilterValues((prev) => {
            const next = { ...prev };
            Object.keys(filters).forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(fromUrl.values, key)) {
                    next[key] = fromUrl.values[key];
                } else {
                    next[key] = emptyValueForFilter(filters[key]);
                }
            });
            return next;
        });
        setFilterLookups((prev) => ({ ...prev, ...fromUrl.lookups }));
        setSearchText(nextSearchText);
        setTimeout(() => {
            syncingFromUrlRef.current = false;
        }, 0);
    }, [urlSearch, filters]);

    // filter state -> URL (pushState) when user changes filters.
    useEffect(() => {
        if (!filters || Object.keys(filters).length === 0) return;
        if (syncingFromUrlRef.current) return;

        const baseSearch = buildSearchParamsFromFilterState({ filterValues, resolvedLookups });
        const params = new URLSearchParams(baseSearch.startsWith('?') ? baseSearch.slice(1) : baseSearch);

        const trimmedSearch = String(searchText || '').trim();
        if (trimmedSearch) params.set('search', trimmedSearch);
        else params.delete('search');

        const nextSearch = params.toString() ? `?${params.toString()}` : '';
        if ((urlSearch || '') === nextSearch) return;

        if (pushStateDebounceRef.current) {
            clearTimeout(pushStateDebounceRef.current);
        }
        pushStateDebounceRef.current = setTimeout(() => {
            const nextUrl =
                `${window.location.pathname}${nextSearch}${window.location.hash || ''}`;
            window.history.pushState({}, '', nextUrl);
            window.dispatchEvent(new Event('locationchange'));
            setUrlSearch(nextSearch);
            pushStateDebounceRef.current = null;
        }, 250);
    }, [filterValues, resolvedLookups, filters, urlSearch, searchText]);

    useEffect(() => {
        return () => {
            if (pushStateDebounceRef.current) {
                clearTimeout(pushStateDebounceRef.current);
                pushStateDebounceRef.current = null;
            }
        };
    }, []);

    const handleFilterChange = (fieldName, value) => {
        if (isDefaultMaxPrice(fieldName, filters[fieldName], value)) {
            value = '';
        }
        setFilterValues((prev) => ({ ...prev, [fieldName]: value }));
    };

    const activeFilterChips = useMemo(() => {
        const chips = [];
        const trimmedSearch = String(searchText || '').trim();
        if (trimmedSearch) {
            chips.push({
                key: 'search',
                fieldName: 'search',
                label: `${i18n.t('Search')}: ${trimmedSearch}`,
                clear: () => setSearchText(''),
            });
        }

        Object.entries(filters).forEach(([fieldName, fieldConfig]) => {
            if (!fieldConfig?.label) return;
            const template = fieldConfig.template || fieldConfig.type || '';
            const multi = isMultiValueFilter(fieldConfig);
            const value = getFilterFieldValue(filterValues, fieldName, fieldConfig);
            if (isEmptyFilterValue(value)) return;

            const selectOptions = normalizeSelectOptions(fieldConfig.options || fieldConfig.choices || []);

            if (template === 'choice' || template === 'choices' || template === 'radio_cards' || multi) {
                normalizeToArray(value).forEach((v) => {
                    chips.push({
                        key: `${fieldName}:${v}`,
                        fieldName,
                        value: v,
                        many: true,
                        label: `${fieldConfig.label}: ${optionLabelForValue(selectOptions, v)}`,
                    });
                });
                return;
            }

            if (template === 'select' || template === 'select_search') {
                chips.push({
                    key: fieldName,
                    fieldName,
                    label: `${fieldConfig.label}: ${optionLabelForValue(selectOptions, value)}`,
                });
                return;
            }

            chips.push({
                key: fieldName,
                fieldName,
                label: `${fieldConfig.label}: ${value}`,
            });
        });

        return chips;
    }, [filters, filterValues, searchText]);

    const removeActiveChip = (chip) => {
        if (chip.clear) {
            chip.clear();
            return;
        }
        const fieldConfig = filters[chip.fieldName];
        if (chip.many || isMultiValueFilter(fieldConfig)) {
            const next = normalizeToArray(filterValues[chip.fieldName]).filter(
                (x) => String(x) !== String(chip.value)
            );
            handleFilterChange(chip.fieldName, next);
        } else {
            handleFilterChange(chip.fieldName, '');
        }
        // Clear stale SSR/API default so a missing filterValues key cannot resurrect selection.
        if (fieldConfig && !isEmptyFilterValue(fieldConfig.value)) {
            setFilters((prev) => ({
                ...prev,
                [chip.fieldName]: {
                    ...prev[chip.fieldName],
                    value: emptyValueForFilter(prev[chip.fieldName]),
                },
            }));
        }
    };

    const renderChoiceFilterGroup = ({
        fieldName,
        fieldLabel,
        selectOptions,
        value,
        many,
    }) => {
        const optionsKey = `${fieldName}:${selectOptions.map((o) => o.value).join(',')}`;
        // Deduplicate by value so React keys stay stable (API can repeat labels/ids).
        const seen = new Set();
        const uniqueOptions = selectOptions.filter((opt) => {
            const k = String(opt.value);
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });
        return (
            <div key={fieldName} className="mb-2">
                <label className="form-label small">{fieldLabel}</label>
                <CollapsibleChoiceOptions
                    measureKey={optionsKey}
                    role={many ? 'group' : 'radiogroup'}
                    aria-label={fieldLabel}
                >
                    {!many ? (
                        <label
                            className={`store-filter-chip${
                                value === '' || value == null ? ' is-selected' : ''
                            }`}
                        >
                            <input
                                type="radio"
                                name={choiceFilterGroupName(fieldName)}
                                className="visually-hidden"
                                checked={value === '' || value == null || value === undefined}
                                onChange={() => handleFilterChange(fieldName, '')}
                            />
                            {i18n.t('All')}
                        </label>
                    ) : null}
                    {many
                        ? uniqueOptions.map((opt) => {
                            const v = opt.value;
                            const arr = normalizeToArray(value);
                            const selected = arr.map(String).includes(String(v));
                            return (
                                <label
                                    key={String(v)}
                                    className={`store-filter-chip${selected ? ' is-selected' : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        className="visually-hidden"
                                        checked={selected}
                                        onChange={() =>
                                            handleFilterChange(fieldName, toggleManyChoiceValue(arr, v))
                                        }
                                    />
                                    {opt.label}
                                </label>
                            );
                        })
                        : uniqueOptions.map((opt) => {
                            const v = opt.value;
                            const selected = String(value) === String(v);
                            return (
                                <label
                                    key={String(v)}
                                    className={`store-filter-chip${selected ? ' is-selected' : ''}`}
                                >
                                    <input
                                        type="radio"
                                        name={choiceFilterGroupName(fieldName)}
                                        className="visually-hidden"
                                        value={String(v)}
                                        checked={selected}
                                        onChange={() => handleFilterChange(fieldName, v)}
                                    />
                                    {opt.label}
                                </label>
                            );
                        })}
                </CollapsibleChoiceOptions>
            </div>
        );
    };

    const renderFilterField = (fieldName, fieldConfig) => {
        if (!fieldConfig || !fieldConfig.label) return null;
        const template = fieldConfig.template || fieldConfig.type || 'select_search';
        const many = isMultiValueFilter(fieldConfig);
        const selectOptions = normalizeSelectOptions(fieldConfig.options || fieldConfig.choices || []);
        const storedValue = getFilterFieldValue(filterValues, fieldName, fieldConfig);
        const value = template === 'input_range'
            ? maxPriceDisplayValue(fieldName, fieldConfig, storedValue)
            : storedValue;

        if (template === 'choice' || template === 'choices' || template === 'radio_cards') {
            return renderChoiceFilterGroup({
                fieldName,
                fieldLabel: fieldConfig.label,
                selectOptions,
                value,
                many,
            });
        }

        const optionsForRenderer = selectOptions.map((o) => ({ id: o.value, label: o.label }));
        const onFieldChange = (e) => handleFilterChange(fieldName, e.value);

        const fieldForRenderer = {
            fieldName,
            label: fieldConfig.label,
            template: template,
            options: optionsForRenderer,
            many,
            allowNull: !many && (template === 'select' || template === 'select_search'),
            nullLabel: i18n.t('All'),
            readOnly: false,
            style:
                template === 'input'
                    ? { class: 'form-control-sm', placeholder: fieldConfig.placeholder }
                    : template === 'select'
                      ? { class: 'form-select-sm' }
                      : {},
            ...(template === 'input' ? { inputType: 'text' } : {}),
            ...(template === 'input_range' ? {
                min: fieldConfig.min ?? 0,
                max: fieldConfig.max,
                step: fieldConfig.step ?? 1,
            } : {}),
        };
        const rendererValues = value === storedValue
            ? filterValues
            : { ...filterValues, [fieldName]: value };

        return (
            <div key={fieldName} className="mb-2">
                <label className="form-label small">{fieldConfig.label}</label>
                <div className="d-flex gap-1 flex-wrap align-items-start w-100">
                    <div className="flex-grow-1 min-w-0">
                        <FieldRenderer
                            id={`filter-store-${fieldName}`}
                            index={0}
                            field={fieldForRenderer}
                            values={rendererValues}
                            onChange={onFieldChange}
                            excludeWrapper={true}
                            excludeLabel={true}
                            template={template}
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="store-filter-pane">
            {filtersLoading ? (
                <div className="placeholder-glow">
                    <span className="placeholder col-12"></span>
                    <span className="placeholder col-10"></span>
                    <span className="placeholder col-11"></span>
                </div>
            ) : filtersError ? (
                <h6 className="text-danger">{i18n.t('Something went wrong loading filters')}</h6>
            ) : (
                <>
                    {activeFilterChips.length > 0 ? (
                        <div className="d-flex flex-wrap gap-1 mb-3" aria-label={i18n.t('Filters')}>
                            {activeFilterChips.map((chip) => (
                                <button
                                    key={chip.key}
                                    type="button"
                                    className="store-filter-chip store-filter-chip-summary is-selected"
                                    onClick={() => removeActiveChip(chip)}
                                    title={`${i18n.t('Remove')}: ${chip.label}`}
                                    aria-label={`${i18n.t('Remove')}: ${chip.label}`}
                                >
                                    {chip.label}
                                    <span aria-hidden="true">×</span>
                                </button>
                            ))}
                        </div>
                    ) : null}

                    <div className="mb-3">
                        <label htmlFor="store-filter-search" className="form-label small mb-1">
                            {i18n.t('Search')}
                        </label>
                        <input
                            id="store-filter-search"
                            type="search"
                            className="form-control form-control-sm"
                            value={searchText}
                            placeholder={i18n.t('Search products')}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </div>
                    <div className="d-flex d-md-none align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">{i18n.t('Filters')}</h6>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            aria-expanded={isFiltersOpen}
                            aria-controls="store-filters-collapse"
                            onClick={() => setIsFiltersOpen((v) => !v)}
                        >
                            {isFiltersOpen ? i18n.t('Hide') : i18n.t('Show')}
                        </button>
                    </div>

                    <div className="d-none d-md-block mb-2">
                        <h6 className="mb-0">{i18n.t('Filters')}</h6>
                    </div>

                    <div
                        id="store-filters-collapse"
                        className={`${isFiltersOpen ? 'd-block' : 'd-none'} d-md-block`}
                    >
                        {Object.entries(filters).map(([name, config]) => renderFilterField(name, config))}
                    </div>
                </>
            )}
        </div>
    );
}
